import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";

const StyledSidebar = styled(Sidebar)`
  width: 300px !important;
  min-width: 300px !important;
  height: 100vh;
  min-height: 100vh;
  border: none !important;
  background-color: ${({ theme }) => theme.palette.common.white};
  border-radius: 0 20px 20px 0;
  display: none;
  visibility: hidden;

  @media (min-width: 1024px) {
    display: block;
    visibility: visible;
  }

  &.ps-collapsed {
    width: 100px !important;
    min-width: 100px !important;

    & > .ps-sidebar-container {
      padding: 50px 16px 55px;
    }
  }

  & > .ps-sidebar-container {
    width: 100%;
    background-color: ${({ theme }) => theme.palette.common.white};
    border-radius: 0 10px 10px 0;
    padding: 50px 30px 40px;
    box-sizing: border-box;
    overflow: visible;
    transition: all 0.3s linear;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 5px;
  }
`;

const StyledSidebarContainer = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 50%;
  gap: 32px;
`;

const StyledSidebarHeader = styled("div")`
  position: relative;
  width: 100%;
  display: flex;
  box-sizing: border-box;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid #d1d9e3;
  transition: all 0.3s linear;
`;

const StyledSidebarLogo = styled(Link)<{ collapsed?: number }>`
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: ${({ collapsed }) => (collapsed === 1 ? "6px" : "0")};
  transition: padding 0.3s linear;

  & > svg {
    width: 100%;
  }
`;

const StyledCollapseBtn = styled("button")<{ collapsed?: number }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: ${({ collapsed }) => (collapsed === 1 ? "none" : "flex")};
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.palette.customGrey.soft};
  transition: background-color 0.15s linear, transform 0.15s linear;
  border: none;
  padding: 4px;
  outline: none;
  cursor: pointer;
  transform: ${({ collapsed }) => (collapsed === 1 ? "scale(0)" : "none")};
  visibility: ${({ collapsed }) => (collapsed === 1 ? "hidden" : "visible")};

  & > svg {
    width: 100%;
    transition: stroke 0.15s linear;
    transform: rotate(90deg);
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.blue.main};

    & > svg {
      transition: stroke 0.15s linear;
      stroke: ${({ theme }) => theme.palette.common.white};
    }
  }
`;

const StyledUnCollapseBtn = styled(StyledCollapseBtn)`
  display: flex;
  position: absolute;
  bottom: 24px;
  right: -28px;
  transition: ${({ collapsed }) =>
    collapsed === 1
      ? "transform 0.3s linear 0.3s, background-color 0.15s linear"
      : "transform 0.1s linear, background-color 0.15s linear"};
  transform: ${({ collapsed }) => (collapsed === 1 ? "scale(1)" : "scale(0)")};
  visibility: ${({ collapsed }) => (collapsed === 1 ? "visible" : "hidden")};

  & > svg {
    transform: rotate(-90deg);
  }
`;

const StyledSidebarMenu = styled(Menu)<{ tooltipIsVisible?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: ${({ tooltipIsVisible }) =>
    tooltipIsVisible === 1 ? "visible" : "hidden"};

  & > ul {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &::-webkit-scrollbar {
    width: 0;
  }

  & .sidebar-switch {
    cursor: default;
  }
`;

const StyledSidebarMenuItem = styled(MenuItem)<{
  collapsed?: number;
}>`
  width: max-content !important;
  padding: 0;
  user-select: none;

  & > .ps-menu-button {
    width: 100%;
    padding: ${({ collapsed }) =>
      collapsed === 1 ? "0 23px" : "0"} !important;
    gap: 16px;
    height: 24px !important;
    transition: padding 0.3s linear;
    background-color: transparent !important;

    &.sidebar__btn {
      width: max-content;
      display: flex;
      box-sizing: border-box;
      align-items: center;
      gap: 16px;
      flex-shrink: 0;
      border: none;
      outline: none;
      background: transparent;
    }

    & .ps-menu-icon {
      margin-right: 0;
      height: 22px;
      width: 22px;
      min-width: 22px;

      & > svg {
        width: 100%;
        stroke: ${({ theme }) => theme.palette.customGrey.dark};
        transition: all 0.15s linear;
      }
    }

    & .ps-menu-label {
      font-family: ${({ theme }) => theme.typography.fontFamily};
      font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
      color: ${({ theme }) => theme.palette.customGrey.dark};
      font-weight: 300;
      visibility: ${({ collapsed }) =>
        collapsed === 1 ? "hidden" : "visible"};
    }

    &:hover {
      background-color: transparent !important;

      & svg {
        stroke: ${({ theme }) => theme.palette.blue.main};
        transition: all 0.15s linear;
      }
    }

    &.ps-active {
      & .ps-menu-label {
        color: ${({ theme }) => theme.palette.common.black};
        font-weight: 500;
      }
    }

    &.ps-disabled {
      & .ps-menu-label {
        color: ${({ theme }) => theme.palette.customGrey.main};
      }

      & svg {
        stroke: ${({ theme }) => theme.palette.customGrey.main};
      }
    }
  }
`;

const StyledSidebarSwitchItem = styled("li")<{ collapsed?: number }>`
  width: max-content;
  padding: 0;
  user-select: none;
  max-width: 100%;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  gap: 16px;
  background: transparent;
  transition: all 0.3s linear;
  flex-shrink: 0;
  padding: ${({ collapsed }) => (collapsed === 1 ? "0 13px" : "0")};
  transition: padding 0.3s linear;
  cursor: pointer;
  text-align: left;
  cursor: default;

  & > .sidebar-switch__text {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
    line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
    color: ${({ theme }) => theme.palette.customGrey.dark};
    font-weight: 300;
    visibility: ${({ collapsed }) => (collapsed === 1 ? "hidden" : "visible")};
    white-space: nowrap;
  }
`;

const StyledSidebarFooter = styled("div")`
  width: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  padding-top: 32px;
  gap: 24px;
  border-top: 1px solid #d1d9e3;
  transition: all 0.3s linear;
  overflow: hidden;
`;

const StyledSidebarVersionContainer = styled("div")`
  width: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  padding-top: 50px;
`;

const StyledSidebarUser = styled("button")<{ collapsed?: number }>`
  width: max-content;
  max-width: 100%;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  gap: 16px;
  border: none;
  outline: none;
  background: transparent;
  transition: all 0.3s linear;
  flex-shrink: 0;
  padding: ${({ collapsed }) => (collapsed === 1 ? "0 18px" : "0")};
  transition: padding 0.3s linear;
  cursor: pointer;
  text-align: left;
`;

const StyledSidebarUserInfo = styled("div")<{ collapsed?: number }>`
  max-width: 190px;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.common.black};
  user-select: none;
  visibility: ${({ collapsed }) => (collapsed === 1 ? "hidden" : "visible")};
`;

const StyledSidebarLogOutBtn = styled("button")<{ collapsed?: number }>`
  width: max-content;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: ${({ collapsed }) => (collapsed === 1 ? "0 20px" : "0")};
  transition: padding 0.3s linear, color 0.15s linear;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
  line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
  color: ${({ theme }) => theme.palette.customGrey.dark};
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;

  & > .log-out-btn__icon {
    height: 22px;
    width: 22px;
    min-width: 22px;

    & > svg {
      width: 100%;
      stroke: ${({ theme }) => theme.palette.customGrey.dark};
      transition: all 0.15s linear;
    }
  }

  & > .log-out-btn__text {
    visibility: ${({ collapsed }) => (collapsed === 1 ? "hidden" : "visible")};
  }

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.blue.main};

    & svg {
      stroke: ${({ theme }) => theme.palette.blue.main};
      transition: all 0.15s linear;
    }
  }
`;

export {
  StyledSidebarContainer,
  StyledSidebar,
  StyledSidebarHeader,
  StyledSidebarLogo,
  StyledCollapseBtn,
  StyledUnCollapseBtn,
  StyledSidebarMenu,
  StyledSidebarMenuItem,
  StyledSidebarSwitchItem,
  StyledSidebarFooter,
  StyledSidebarUser,
  StyledSidebarUserInfo,
  StyledSidebarLogOutBtn,
  StyledSidebarVersionContainer,
};
